// ============================================================================
// EXPENSE TYPE SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { expenseTypeEndpoints, EXPENSE_TYPE_BASE_PATH } from '../endpoints/expenseType.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  ExpenseType,
  ExpenseTypeListItem,
  CreateExpenseTypeRequest,
  UpdateExpenseTypeRequest,
  ExpenseTypeSearchRequest,
} from '../types/expenseType.types';

class ExpenseTypeService {
  async getAll(params?: ExpenseTypeSearchRequest): Promise<PaginatedResponse<ExpenseTypeListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(EXPENSE_TYPE_BASE_PATH, params)
      : expenseTypeEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<ExpenseType>> {
    const response = await get<ApiResponse<ExpenseType>>(expenseTypeEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<ExpenseType>> {
    const response = await get<ApiResponse<ExpenseType>>(expenseTypeEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateExpenseTypeRequest): Promise<ApiResponse<ExpenseType>> {
    const response = await post<ApiResponse<ExpenseType>>(expenseTypeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateExpenseTypeRequest): Promise<ApiResponse<ExpenseType>> {
    const response = await put<ApiResponse<ExpenseType>>(expenseTypeEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(expenseTypeEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${expenseTypeEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : expenseTypeEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const expenseTypeService = new ExpenseTypeService();
export default expenseTypeService;
